/**
 * PhotoLightbox — full-screen photo viewer.
 * Backdrop click closes; ←/→ keys navigate; ESC closes; X button top-right.
 * Metadata strip below image (uploader, relative time, caption).
 */

import { useEffect, type JSX } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import type { PhotoWithUrl } from './PhotoGallery';

interface PhotoLightboxProps {
  photos: PhotoWithUrl[];
  activeIndex: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

function relativeTime(iso: string): string {
  const d = new Date(iso).getTime();
  if (isNaN(d)) return '—';
  const m = Math.floor((Date.now() - d) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function PhotoLightbox({
  photos,
  activeIndex,
  onClose,
  onNavigate,
}: PhotoLightboxProps): JSX.Element {
  const photo = photos[activeIndex];

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' && activeIndex < photos.length - 1) {
        onNavigate(activeIndex + 1);
      } else if (e.key === 'ArrowLeft' && activeIndex > 0) {
        onNavigate(activeIndex - 1);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeIndex, photos.length, onClose, onNavigate]);

  if (!photo) return <></>;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Photo viewer"
    >
      {/* Backdrop sibling-button — keyboard close is via the window-level
          ESC listener above; click closes the modal */}
      <button
        type="button"
        className="absolute inset-0 bg-black/85 cursor-default"
        onClick={onClose}
        aria-label="Close photo viewer"
        tabIndex={-1}
      />

      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 text-ink-bright hover:text-ink-primary p-2"
        aria-label="Close"
      >
        <X size={24} />
      </button>

      {activeIndex > 0 && (
        <button
          type="button"
          onClick={() => onNavigate(activeIndex - 1)}
          className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-bright hover:text-ink-primary p-2"
          aria-label="Previous photo"
        >
          <ChevronLeft size={32} />
        </button>
      )}

      {activeIndex < photos.length - 1 && (
        <button
          type="button"
          onClick={() => onNavigate(activeIndex + 1)}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-ink-bright hover:text-ink-primary p-2"
          aria-label="Next photo"
        >
          <ChevronRight size={32} />
        </button>
      )}

      <img
        src={photo.signed_url}
        alt={photo.caption || 'Photo'}
        className="relative max-w-[90vw] max-h-[80vh] object-contain pointer-events-none"
      />

      <div className="relative mt-4 text-xs text-ink-bright bg-surface-1/80 rounded px-3 py-2 max-w-[90vw] pointer-events-none">
        <div className="flex items-center gap-3">
          <span className="font-medium">{photo.uploaded_by_name || 'Unknown'}</span>
          <span className="text-ink-muted">{relativeTime(photo.uploaded_at)}</span>
          <span className="text-ink-muted font-mono">
            {activeIndex + 1} / {photos.length}
          </span>
        </div>
        {photo.caption && (
          <p className="mt-1 text-ink-secondary whitespace-pre-wrap">{photo.caption}</p>
        )}
      </div>
    </div>
  );
}
